<template>
  <div class="map-query-container">
    <div class="search-box">
      <div class="search-wrapper">
        <a-select v-model="searchType" size="small" class="type-select" :dropdown-match-select-width="false"
          :get-popup-container="(triggerNode) => triggerNode.parentNode">
          <a-select-option value="Point">管点</a-select-option>
          <a-select-option value="LineString">管线</a-select-option>
        </a-select>
        <a-input-search v-model="queryValue" placeholder="搜索点号/编码..." size="small" allow-clear @search="onSearch"
          @focus="showResults = true" />
      </div>
    </div>

    <div v-if="showResults && results.length > 0" class="search-results">
      <div class="results-header">
        <span>共 {{ results.length }} 条结果</span>
        <a-icon type="close" class="close-btn" @click="showResults = false" />
      </div>
      <div class="results-list">
        <div v-for="item in results" :key="item.id" class="result-item" @click="handleSelect(item)">
          <div class="item-title">
            <a-icon :type="item.type === 'LineString' ? 'branches' : 'environment'" class="type-icon" />
            <template v-if="item.type === 'LineString'">
              {{ item.startClassNo || '未知' }} - {{ item.endClassNo || '未知' }}
            </template>
            <template v-else>
              {{ item.class_no || '未知要素' }}
            </template>
          </div>
          <div class="item-desc">
            <template v-if="item.type === 'LineString'">
              物探点号 ({{ item.startGeophysicalPointNumber || '无' }} - {{ item.endGeophysicalPointNumber || '无' }})
            </template>
            <template v-else>
              物探点号 ({{ item.geophysical_point_number }})
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MapQuery',
  props: {
    // 允许父组件传入地图数据用于前端过滤搜索（性能更好）
    markerDataGroups: {
      type: Object,
      default: () => ({})
    },
    // 传入管线 source 用于匹配要素
    lineSource: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      searchType: 'Point',
      queryValue: '',
      results: [],
      showResults: false
    }
  },
  methods: {
    async onSearch (val) {
      const searchVal = val ? val.trim() : '';
      if (!searchVal) {
        this.results = [];
        return;
      }

      if (this.searchType === 'Point') {
        try {
          const res = await this.$http.get(`/pipe-network/gis/pipe-networks/query/point/${searchVal}`);
          const data = res.data;
          const resultList = Array.isArray(data) ? data : (data ? [data] : []);

          const matched = [];
          resultList.forEach(item => {
            // 在本地寻找对应的 feature 以支持定位
            let foundFeature = null;
            Object.keys(this.markerDataGroups).forEach(type => {
              if (foundFeature) return;
              const group = this.markerDataGroups[type];
              const f = group.find(feature => {
                const props = feature.getProperties();
                return String(props.id) === String(item.id);
              });
              if (f) foundFeature = f;
            });

            matched.push({
              id: item.id,
              geophysical_point_number: item.geophysicalPointNumber,
              class_no: item.class_no,
              point: item.point,
              type: 'Point',
              feature: foundFeature
            });
          });

          this.results = matched.slice(0, 50);
          this.showResults = true;

          if (this.results.length === 0) {
            this.$message.warning('未搜索到匹配项');
          }
        } catch (e) {
          console.error('搜索失败:', e);
          this.$message.error('搜索服务异常');
        }
      } else {
        try {
          const res = await this.$http.get(`/pipe-network/gis/pipe-lines/query/line/${searchVal}`);
          const data = res.data;
          const resultList = Array.isArray(data) ? data : (data ? [data] : []);

          const matched = [];
          resultList.forEach(item => {
            // 在本地寻找对应的 feature 以支持定位
            let foundFeature = null;
            if (this.lineSource) {
              foundFeature = this.lineSource.getFeatureById(item.id);
              // 如果 setId 没生效，尝试通过属性寻找
              if (!foundFeature) {
                foundFeature = this.lineSource.getFeatures().find(f => {
                  const props = f.getProperties();
                  return String(props.id) === String(item.id);
                });
              }
            }

            matched.push({
              id: item.id,
              startClassNo: item.startClassNo,
              endClassNo: item.endClassNo,
              startGeophysicalPointNumber: item.startGeophysicalPointNumber,
              endGeophysicalPointNumber: item.endGeophysicalPointNumber,
              type: 'LineString',
              feature: foundFeature
            });
          });

          this.results = matched.slice(0, 50);
          this.showResults = true;

          if (this.results.length === 0) {
            this.$message.warning('未搜索到匹配项');
          }
        } catch (e) {
          console.error('搜索失败:', e);
          this.$message.error('搜索服务异常');
        }
      }
    },
    handleSelect (item) {
      this.showResults = false;
      this.$emit('select', item);
    }
  }
}
</script>

<style lang="less" scoped>
.map-query-container {
  position: absolute;
  bottom: 20px;
  left: calc(50% - 360px); // 位于中心工具栏左侧
  transform: translateX(-50%);
  z-index: 1001;
  width: 240px;

  .search-box {
    background: rgba(255, 255, 255, 0.9);
    padding: 4px 6px;
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
    border: 1px solid #ddd;

    .search-wrapper {
      display: flex;
      align-items: center;

      .type-select {
        width: 80px;
        flex-shrink: 0;

        /deep/ .ant-select-selection {
          background: transparent;
          border: none;
          box-shadow: none !important;
        }

        /deep/ .ant-select-selection-selected-value {
          padding-right: 15px;
        }
      }

      &::after {
        content: "";
        width: 1px;
        height: 14px;
        background: #e8e8e8;
        margin: 0 4px;
      }
    }

    /deep/ .ant-input {
      background: transparent;
      border: none;
      box-shadow: none !important;
    }
  }

  .search-results {
    position: absolute;
    bottom: 50px;
    left: 0;
    width: 240px;
    background: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #ebeef5;
    max-height: 400px;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .results-header {
      padding: 8px 12px;
      border-bottom: 1px solid #f0f0f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #fafafa;
      font-size: 12px;
      color: #999;

      .close-btn {
        cursor: pointer;

        &:hover {
          color: #1890ff;
        }
      }
    }

    .results-list {
      overflow-y: auto;
      padding: 4px 0;

      .result-item {
        padding: 8px 12px;
        cursor: pointer;
        transition: background 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        .item-title {
          font-size: 14px;
          color: #333;
          font-weight: 500;
          display: flex;
          align-items: center;

          .type-icon {
            margin-right: 8px;
            color: #1890ff;
          }
        }

        .item-desc {
          font-size: 12px;
          color: #999;
          margin-top: 2px;
          padding-left: 22px;
        }
      }
    }
  }
}
</style>
